// Shared plumbing of lane B2's request screens: reads that hold the capture settle signal until their data arrives, the
// field words by server column, and the refusal sentences (his rule sentence by rule code; the server's own detail
// text is never rendered).

import { AppError } from "@/api/runtime";
import { REQUEST_STATUS, ROLE_LABELS, SCORECARD_LABELS } from "@/mc";
import { V1_FIELD_WORDS } from "./compat";
import { REQUEST_FIELD_LABELS as F, REQUEST_PAGE_LABELS as P, REQUEST_RULES as R, TIMING_LABELS as T } from "./labels";

export { useSettledRead } from "@/views/shared/settledRead";

/** The words of a request column (the server's field key), his where he has one. */
export const FIELD_WORD: Readonly<Record<string, string>> = {
  title: F.title,
  level: SCORECARD_LABELS.level,
  intended_hm_id: ROLE_LABELS.hmAbbr,
  hiring_manager_id: ROLE_LABELS.hmAbbr,
  reason: F.newReplacement,
  vacating_person_ref: F.replacementEmployee,
  locations: F.location,
  openings_requested: F.openings,
  is_people_manager: F.peopleManager,
  direct_reports: F.directReports,
  posting_scope: F.postingType,
  notes: F.notes,
  department: F.department,
  job_family: F.jobFamily,
  area_of_interest: F.areaOfInterest,
  job_code: F.jobCode,
  org_alignment: F.orgAlignment,
  budget_status: F.headcountApproval,
  cost_center: F.costCenter,
  justification: F.justification,
  final_posting_validated: F.finalPostingValidation,
  job_description: P.currentJd,
  target_start: T.targetStartDate,
  urgency: T.urgency,
  target_comp_min: T.targetCompRange,
  target_comp_max: T.targetCompRange,
  currency: T.targetCompRange,
  position_no: T.positionNumber,
  business_unit: V1_FIELD_WORDS.businessUnit,
  brands: V1_FIELD_WORDS.brands,
  reports_to_title: V1_FIELD_WORDS.reportsToTitle,
  vacating_last_day: V1_FIELD_WORDS.lastDay,
  level_and_title_unchanged: V1_FIELD_WORDS.levelAndTitleUnchanged,
};

/** The rule code of a refusal ("RULE_UNMET / not_certified: …" → "not_certified"). */
export function ruleCode(error: AppError): string | null {
  return /(?:RULE_UNMET|SOD_CONFLICT) \/ ([a-z_]+)/.exec(error.message)?.[1] ?? null;
}

/** Rule code → his sentence. */
export const REFUSAL_SENTENCES: Readonly<Record<string, string>> = {
  request_incomplete: R.requiredFields,
  final_posting_validation: R.requiredFields,
  hiring_manager_required: R.hrbpCreates,
  requester_not_hrbp: R.hrbpCreates,
  reviewer_is_originator: R.finalApprover,
  reviewer_override_reason: R.finalApprover,
  reviewer_not_hrbp: R.finalApprover,
  already_claimed: R.finalApprover,
  hm_is_originator: R.distinctCertification,
  not_certified: R.distinctCertification,
  payfactors_attestation: R.payfactors,
  correction_reason_required: R.hmCorrects,
  change_reason: R.fieldOwnership,
  job_description_reason: R.payfactors,
  stale_version: R.optimisticConcurrency,
  review_stale: R.optimisticConcurrency,
  checks_unacknowledged: R.finalApprover,
  request_status: R.chain,
};

/** His sentence for a 4xx refusal, or null when the failure is not a refusal (a 5xx or a lost connection). */
export function refusalSentence(error: unknown): string | null {
  if (!(error instanceof AppError) || error.status < 400 || error.status >= 500) return null;
  if (error.code === "FIELD_NOT_OWNED") return R.fieldOwnership;
  const code = ruleCode(error);
  if (code && REFUSAL_SENTENCES[code]) return REFUSAL_SENTENCES[code]!;
  if (error.code === "SOD_CONFLICT") return R.finalApprover;
  return R.chain;
}

/** The server's field errors as the field words they name. */
export function fieldErrorWords(error: unknown): string[] {
  if (!(error instanceof AppError)) return [];
  return Object.keys(error.fieldErrors).map((key) => FIELD_WORD[key] ?? key);
}

/** A request's status → his word (V1 statuses map onto his chain). */
export const STATUS_WORD: Readonly<Record<string, string>> = {
  drafted: REQUEST_STATUS.draft,
  returned: REQUEST_STATUS.returned,
  dormant: REQUEST_STATUS.dormant,
  pending_hm_cert: REQUEST_STATUS.pendingHmCertification,
  submitted: REQUEST_STATUS.pendingHrbpApproval,
  question_raised: REQUEST_STATUS.pendingHrbpApproval,
  approved: REQUEST_STATUS.approved,
  converted: REQUEST_STATUS.approved,
};
